/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Developer API Request Volume Interactive SVG Chart (Task 10.6.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import { Activity } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import {
  useDeveloperAnalyticsStore,
  TelemetryPoint,
} from '../../../stores/useDeveloperAnalyticsStore';

export const RequestVolumeChart: React.FC = () => {
  const { t } = useTranslation();
  const { telemetryData } = useDeveloperAnalyticsStore();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (!telemetryData || telemetryData.length === 0) {
    return null;
  }

  // Dimensions
  const svgWidth = 800;
  const svgHeight = 220;
  const paddingX = 40;
  const paddingTop = 20;
  const paddingBottom = 35;
  const chartWidth = svgWidth - paddingX * 2;
  const chartHeight = svgHeight - paddingTop - paddingBottom;

  // Max value calculation for scale
  const maxReq = Math.max(...telemetryData.map((d) => d.totalRequests), 10);
  const yMax = Math.ceil((maxReq * 1.15) / 10) * 10;

  // Helper coordinate mapper
  const getX = (index: number) => {
    if (telemetryData.length <= 1) return paddingX + chartWidth / 2;
    return paddingX + (index / (telemetryData.length - 1)) * chartWidth;
  };

  const getY = (val: number) => {
    return paddingTop + chartHeight - (val / yMax) * chartHeight;
  };

  // Build SVG path strings
  const totalPoints = telemetryData.map((d, i) => `${getX(i)},${getY(d.totalRequests)}`);
  const successPoints = telemetryData.map((d, i) => `${getX(i)},${getY(d.successfulRequests)}`);

  const totalLinePath = `M ${totalPoints.join(' L ')}`;
  const successLinePath = `M ${successPoints.join(' L ')}`;

  // Area path closing at baseline
  const areaPath = `M ${getX(0)},${paddingTop + chartHeight} L ${totalPoints.join(' L ')} L ${getX(
    telemetryData.length - 1
  )},${paddingTop + chartHeight} Z`;

  const hoveredPoint: TelemetryPoint | null =
    hoveredIndex !== null && telemetryData[hoveredIndex] ? telemetryData[hoveredIndex] : null;

  // Select evenly spaced x-axis tick indices (up to 6)
  const step = Math.max(1, Math.floor(telemetryData.length / 5));
  const tickIndices = telemetryData.map((_, i) => i).filter((i) => i % step === 0 || i === telemetryData.length - 1);

  return (
    <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4">
      {/* Chart Header & Legend */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <Activity className="size-4 text-primary" />
            <h3 className="text-sm font-bold font-heading text-foreground">
              {t('apikeys.chartRequestVolume')}
            </h3>
          </div>
          <p className="text-xs text-foreground-muted">
            {t('apikeys.chartRequestVolumeDesc')}
          </p>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-primary" />
            <span className="text-foreground-muted text-[11px]">Total Requests</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-emerald-500" />
            <span className="text-foreground-muted text-[11px]">2xx Success</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="size-2.5 rounded-full bg-amber-500" />
            <span className="text-foreground-muted text-[11px]">429 Limited</span>
          </div>
        </div>
      </div>

      {/* SVG Canvas */}
      <div className="relative w-full overflow-hidden">
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="w-full h-48 sm:h-56 select-none"
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <defs>
            <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-primary, #6366f1)" stopOpacity="0.28" />
              <stop offset="100%" stopColor="var(--color-primary, #6366f1)" stopOpacity="0.01" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => {
            const y = paddingTop + chartHeight * pct;
            return (
              <line
                key={`grid-${i}`}
                x1={paddingX}
                y1={y}
                x2={svgWidth - paddingX}
                y2={y}
                stroke="currentColor"
                className="text-surface-border/50"
                strokeDasharray="4 4"
                strokeWidth="1"
              />
            );
          })}

          {/* Area fill */}
          <path d={areaPath} fill="url(#areaGradient)" />

          {/* Lines */}
          <path
            d={totalLinePath}
            fill="none"
            stroke="var(--color-primary, #6366f1)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d={successLinePath}
            fill="none"
            stroke="#10b981"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="2 1"
          />

          {/* Rate limited highlight markers */}
          {telemetryData.map((d, i) => {
            if (d.rateLimitedRequests === 0) return null;
            return (
              <circle
                key={`rate-${i}`}
                cx={getX(i)}
                cy={getY(d.totalRequests)}
                r="3.5"
                fill="#f59e0b"
                stroke="#ffffff"
                strokeWidth="1.5"
              />
            );
          })}

          {/* Interactive Hover Vertical Line & Cursor Dots */}
          {hoveredIndex !== null && (
            <g>
              <line
                x1={getX(hoveredIndex)}
                y1={paddingTop}
                x2={getX(hoveredIndex)}
                y2={paddingTop + chartHeight}
                stroke="var(--color-primary, #6366f1)"
                strokeWidth="1.5"
                strokeDasharray="3 3"
              />
              <circle
                cx={getX(hoveredIndex)}
                cy={getY(telemetryData[hoveredIndex].totalRequests)}
                r="5"
                fill="var(--color-primary, #6366f1)"
                stroke="#ffffff"
                strokeWidth="2"
              />
            </g>
          )}

          {/* Transparent Hover Hitbox Columns */}
          {telemetryData.map((_, i) => {
            const colWidth = chartWidth / telemetryData.length;
            const x = getX(i) - colWidth / 2;
            return (
              <rect
                key={`hit-${i}`}
                x={x}
                y={paddingTop}
                width={colWidth}
                height={chartHeight}
                fill="transparent"
                className="cursor-crosshair"
                onMouseEnter={() => setHoveredIndex(i)}
              />
            );
          })}

          {/* X Axis Time Labels */}
          {tickIndices.map((i) => (
            <text
              key={`tick-${i}`}
              x={getX(i)}
              y={svgHeight - 10}
              textAnchor="middle"
              className="text-[10px] fill-foreground-muted font-mono"
            >
              {telemetryData[i].label}
            </text>
          ))}
        </svg>

        {/* Floating Tooltip */}
        {hoveredPoint && hoveredIndex !== null && (
          <div
            className="absolute top-2 pointer-events-none transform -translate-x-1/2 p-2.5 rounded-xl border border-surface-border bg-surface-elevated/95 backdrop-blur-md shadow-lg text-xs space-y-1 transition-all z-20"
            style={{
              left: `${(getX(hoveredIndex) / svgWidth) * 100}%`,
            }}
          >
            <div className="flex items-center justify-between gap-3 border-b border-surface-border pb-1 font-mono text-[11px] font-bold text-foreground">
              <span>{hoveredPoint.label}</span>
              <span className="text-primary">{hoveredPoint.totalRequests} reqs</span>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
              <span className="text-emerald-500 font-medium">
                Success: {hoveredPoint.successfulRequests}
              </span>
              <span className="text-amber-500 font-medium">
                Rate-limited: {hoveredPoint.rateLimitedRequests}
              </span>
              <span className="text-foreground-muted">
                Latency: {hoveredPoint.avgLatencyMs}ms
              </span>
              <span className="text-foreground-muted">
                Tokens: {hoveredPoint.tokensUsed.toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
